/* SamplePreview — shared visual primitives for the empty-state "Sample
 * preview" experience.
 *
 * Exports:
 *   <SamplePreviewBanner>   — explainer pill at the top of any list
 *   <SampleRowMuted>        — generic 65%-opacity row wrapper with chip
 *   <SampleBuyerDrawer>     — fake 360° drawer with full buyer detail
 *   <SampleOrderDrawer>     — fake order detail
 *   <SampleInvoiceDialog>   — fake invoice detail (modal)
 *   <SampleShipmentDrawer>  — fake shipment tracking detail
 *
 * Hard rules across every drawer:
 *   - No backend calls — every action button is visually present but
 *     disabled with a "Sample row — actions disabled" hover hint.
 *   - Visual: muted slate palette, "SAMPLE" chip in the header, gentle
 *     amber banner inside the drawer reminding the user this is illustrative.
 *   - Closes when user clicks outside, presses Esc, or clicks the X.
 *   - All sample drawers receive their data as props — no fetching.
 */
import { useEffect } from "react";
import {
  Sparkles, X, Ban, Truck, Package, MapPin, CheckCircle2, Clock,
  Receipt, ShieldCheck, TrendingUp, Activity, DollarSign, ExternalLink,
  Mail, Building2, AlertTriangle, Calendar,
} from "lucide-react";
import { Sheet, SheetContent, SheetCloseX, SheetHeader, SheetTitle } from "./ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { sampleMoney, SAMPLE_TIMELINE, SAMPLE_INVOICES, SAMPLE_ORDERS, SAMPLE_SHIPMENTS, SAMPLE_INSURANCE_COVERS } from "../lib/sampleData";

// ─────────────────────────────────────────────────────────────────────────────
// Banner — sits above every sample-data list
// ─────────────────────────────────────────────────────────────────────────────
export function SamplePreviewBanner({ noun, cta, ctaTestId, onCta }) {
  // `noun` = what the list is showing ("Buyers", "Orders", "Invoices", "Shipments")
  // `cta`  = optional primary action label ("Invite your first buyer", etc)
  return (
    <div className="px-5 py-3.5 bg-accent/15 border-b border-border flex flex-wrap items-center justify-between gap-3" data-testid={`sample-${noun.toLowerCase()}-banner`}>
      <div className="flex items-start gap-2.5 min-w-0">
        <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-foreground text-background">
          <Sparkles className="w-3 h-3"/>
        </span>
        <div className="min-w-0">
          <div className="text-[12.5px] font-medium text-foreground/85">
            Sample preview — this is how your {noun} will look
          </div>
          <div className="text-[11px] text-foreground/55 mt-0.5">
            Click any row to see the full detail view. These rows aren't real — they'll disappear the moment you have your first real {noun.toLowerCase()}.
          </div>
        </div>
      </div>
      {cta && onCta && (
        <button
          onClick={onCta}
          data-testid={ctaTestId}
          className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-1.5 rounded-full bg-foreground text-background hover:opacity-90"
        >
          {cta}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SAMPLE chip — small inline marker on every sample row
// ─────────────────────────────────────────────────────────────────────────────
export function SampleChip() {
  return (
    <span className="px-1.5 py-0 rounded text-[9px] mono uppercase tracking-widest bg-foreground/10 text-foreground/55 border border-foreground/15">
      Sample
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample-mode disabled action (rendered inside every drawer)
// ─────────────────────────────────────────────────────────────────────────────
function DisabledSampleAction({ children, icon: Icon }) {
  return (
    <button
      type="button"
      disabled
      title="Sample row — actions disabled until you have real data"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] border border-foreground/15 bg-white text-foreground/40 cursor-not-allowed"
    >
      {Icon && <Icon className="w-3 h-3"/>}
      {children}
    </button>
  );
}

function SampleDrawerExplainer({ kind }) {
  return (
    <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50/40 p-3 text-[11.5px] text-amber-900 flex items-start gap-2" data-testid="sample-drawer-explainer">
      <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
      <div>
        <strong>Sample {kind}.</strong> This view is illustrative — every action button below is disabled. Once you have a real {kind}, this preview disappears and the drawer shows live data.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Buyer 360° drawer
// ─────────────────────────────────────────────────────────────────────────────
export function SampleBuyerDrawer({ buyer, onClose }) {
  if (!buyer) return null;
  const utilization = buyer.credit_limit ? Math.round((buyer.credit_used / buyer.credit_limit) * 100) : 0;
  return (
    <Sheet open={!!buyer} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="sample-buyer-drawer">
        <SheetCloseX/>
        <SheetHeader className="pb-3 border-b border-border">
          <div className="flex items-center justify-between gap-3 pr-8">
            <div>
              <SheetTitle className="flex items-center gap-2 display text-xl tracking-tight">
                {buyer.name}
                <SampleChip/>
              </SheetTitle>
              <div className="text-[12px] text-foreground/55 mt-0.5">{buyer.contact} · {buyer.email} · {buyer.location}</div>
            </div>
          </div>
        </SheetHeader>
        <div className="py-4 space-y-5">
          <SampleDrawerExplainer kind="buyer"/>

          {/* Score + status row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-1">Verqo Trade Score</div>
              <div className="flex items-baseline gap-2">
                <span className="text-[28px] font-semibold tabular-nums">{buyer.verqo_score}</span>
                <span className={`text-[11px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                  buyer.rating_color === "emerald" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-700"
                }`}>{buyer.rating}</span>
              </div>
              <div className="mt-2 text-[11px] text-foreground/55">Based on payment history, business age, and external signals.</div>
            </div>
            <div className="rounded-xl border border-border bg-white p-4">
              <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-1">Credit usage</div>
              <div className="text-[20px] font-semibold tabular-nums">{sampleMoney(buyer.credit_used)}</div>
              <div className="text-[11.5px] text-foreground/55 mt-0.5">of {sampleMoney(buyer.credit_limit)} limit · {utilization}%</div>
              <div className="mt-2 h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                <div className="h-full bg-foreground/80" style={{ width: `${utilization}%` }}/>
              </div>
            </div>
          </div>

          {/* Business details */}
          <div className="rounded-xl border border-border bg-white p-4 space-y-2 text-[12.5px]">
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Business details</div>
            {[
              ["Status",          buyer.status === "connected" ? "✓ Connected" : "Invited"],
              ["Industry",        buyer.industry],
              ["Locations",       `${buyer.locations_count} location${buyer.locations_count === 1 ? "" : "s"}`],
              ["Annual volume",   buyer.annual_volume],
              ["First connected", buyer.first_connected],
              ["Payment terms",   buyer.terms],
              ["Insurance",       buyer.insured ? `${buyer.insurance_carrier} · covered` : "Not covered"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-3">
                <span className="text-foreground/55">{k}</span>
                <span className="text-foreground/85 text-right">{v}</span>
              </div>
            ))}
          </div>

          {/* Open AR */}
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Open accounts receivable</div>
              <DollarSign className="w-3.5 h-3.5 text-foreground/40"/>
            </div>
            <div className="text-[24px] font-semibold tabular-nums">{sampleMoney(buyer.open_ar)}</div>
            <div className="text-[11.5px] text-foreground/55 mt-0.5">{buyer.notes}</div>
          </div>

          {/* Recent activity */}
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2.5">Recent activity</div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {SAMPLE_TIMELINE.map((ev, i) => (
                <div key={i} className="text-[11.5px] flex items-start gap-2 border-b border-border/40 pb-1.5 last:border-0">
                  <div className="shrink-0 w-14 mono text-foreground/55 text-[10.5px] tabular-nums">{ev.at}</div>
                  <div className="flex-1 min-w-0 text-foreground/80">{ev.text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions row — all disabled */}
          <div>
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Available actions <span className="font-normal normal-case ml-1.5 text-foreground/45">(disabled in sample mode)</span></div>
            <div className="flex flex-wrap gap-2">
              <DisabledSampleAction icon={Mail}>Send invite</DisabledSampleAction>
              <DisabledSampleAction icon={ShieldCheck}>Request Allianz cover</DisabledSampleAction>
              <DisabledSampleAction icon={TrendingUp}>Adjust credit limit</DisabledSampleAction>
              <DisabledSampleAction icon={Ban}>Block buyer</DisabledSampleAction>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Order detail drawer
// ─────────────────────────────────────────────────────────────────────────────
export function SampleOrderDrawer({ order, viewerRole = "seller", onClose }) {
  if (!order) return null;
  const tone =
    order.status === "delivered"  ? "bg-emerald-500/15 text-emerald-700" :
    order.status === "in_transit" ? "bg-blue-500/15 text-blue-700" :
    order.status === "processing" ? "bg-amber-500/15 text-amber-800" :
                                     "bg-foreground/10 text-foreground/65";
  // Find the matching sample invoice for this order
  const invoice  = SAMPLE_INVOICES.find(i => i.order_id === order.id);
  const shipment = SAMPLE_SHIPMENTS.find(s => s.order_id === order.id);

  return (
    <Sheet open={!!order} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="sample-order-drawer">
        <SheetCloseX/>
        <SheetHeader className="pb-3 border-b border-border">
          <SheetTitle className="display text-xl tracking-tight flex items-center gap-2 pr-8">
            {order.code}
            <SampleChip/>
          </SheetTitle>
          <div className="text-[12px] text-foreground/55">
            {viewerRole === "seller" ? `Buyer: ${order.buyer_name}` : `Seller: ${order.seller_name}`} · placed {order.created_at}
          </div>
        </SheetHeader>
        <div className="py-4 space-y-5">
          <SampleDrawerExplainer kind="order"/>

          <div className="grid grid-cols-3 gap-3">
            <SampleStat label="Status"     value={
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${tone}`}>
                {order.status.replace(/_/g, " ")}
              </span>
            }/>
            <SampleStat label="Total"      value={sampleMoney(order.total)}/>
            <SampleStat label="Units"      value={`${order.units} units`}/>
          </div>

          {/* Order breakdown */}
          <div className="rounded-xl border border-border bg-white p-4 space-y-1.5 text-[12.5px]">
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Order breakdown</div>
            <KV k="Items subtotal" v={sampleMoney(order.subtotal)}/>
            <KV k="Shipping"       v={sampleMoney(order.shipping_total)}/>
            <div className="border-t border-foreground/10 pt-1.5 mt-1.5 flex justify-between font-semibold">
              <span>Total</span><span className="tabular-nums">{sampleMoney(order.total)}</span>
            </div>
            <div className="text-[11.5px] text-foreground/55 mt-1">{order.items_count} line items · {order.shipping_mode === "seller" ? "Seller arranges shipping" : order.shipping_mode === "buyer_arranged" ? "Buyer arranges shipping" : "verqo Shipping"}</div>
          </div>

          {/* Linked invoice */}
          {invoice && (
            <div className="rounded-xl border border-border bg-white p-4 space-y-1.5 text-[12.5px]">
              <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Linked invoice</div>
              <KV k="Code"   v={<span className="mono">{invoice.code}</span>}/>
              <KV k="Status" v={
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                  invoice.status === "paid" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-800"
                }`}>{invoice.status}</span>
              }/>
              <KV k="Terms"  v={invoice.terms.replace(/_/g, "-").toUpperCase()}/>
              <KV k="Due"    v={new Date(invoice.due_date).toLocaleDateString()}/>
              {invoice.paid_at && <KV k="Paid" v={new Date(invoice.paid_at).toLocaleDateString()}/>}
            </div>
          )}

          {/* Tracking — only if shipment exists */}
          {shipment && (
            <div className="rounded-xl border border-border bg-white p-4 space-y-1.5 text-[12.5px]">
              <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Shipping</div>
              <KV k="Carrier"  v={`${shipment.carrier} · ${shipment.service}`}/>
              <KV k="Tracking" v={<span className="font-mono">{shipment.tracking_number}</span>}/>
              <KV k="Status"   v={shipment.status.replace(/_/g, " ")}/>
              <KV k="Route"    v={`${shipment.origin_city} → ${shipment.destination_city}`}/>
            </div>
          )}

          <div>
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Available actions <span className="font-normal normal-case ml-1.5 text-foreground/45">(disabled in sample mode)</span></div>
            <div className="flex flex-wrap gap-2">
              {viewerRole === "seller" ? (
                <>
                  <DisabledSampleAction icon={Package}>Mark ready to ship</DisabledSampleAction>
                  <DisabledSampleAction icon={Truck}>Book carrier</DisabledSampleAction>
                  <DisabledSampleAction icon={Receipt}>Adjust invoice</DisabledSampleAction>
                </>
              ) : (
                <>
                  <DisabledSampleAction icon={DollarSign}>Pay invoice</DisabledSampleAction>
                  <DisabledSampleAction icon={MapPin}>Track shipment</DisabledSampleAction>
                  <DisabledSampleAction icon={AlertTriangle}>File QI claim</DisabledSampleAction>
                </>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Invoice detail dialog
// ─────────────────────────────────────────────────────────────────────────────
export function SampleInvoiceDialog({ invoice, onClose }) {
  if (!invoice) return null;
  const pt = invoice.shipping_passthrough;
  const itemsSubtotal = Math.max(0, Number(invoice.amount || 0) - (pt?.billed || 0));
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="sample-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="display text-xl tracking-tight flex items-center gap-2">
            Invoice {invoice.code}
            <SampleChip/>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <SampleDrawerExplainer kind="invoice"/>

          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12.5px]">
            <span className="text-foreground/55">Status</span>
            <span className="text-right">
              <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                invoice.status === "paid" ? "bg-emerald-500/15 text-emerald-700" : "bg-amber-500/15 text-amber-800"
              }`}>{invoice.status}</span>
            </span>
            <span className="text-foreground/55">Seller</span>
            <span className="text-right">{invoice.seller_name}</span>
            <span className="text-foreground/55">Buyer</span>
            <span className="text-right">{invoice.buyer_name}</span>
            <span className="text-foreground/55">Terms</span>
            <span className="text-right uppercase">{invoice.terms.replace(/_/g, "-")}</span>
            <span className="text-foreground/55">Due</span>
            <span className="text-right">{new Date(invoice.due_date).toLocaleDateString()}</span>
            {invoice.paid_at && (<>
              <span className="text-foreground/55">Paid</span>
              <span className="text-right">{new Date(invoice.paid_at).toLocaleDateString()} · {invoice.paid_method?.toUpperCase()}</span>
            </>)}
          </div>

          <div className="rounded-lg border border-border bg-secondary/30 p-3.5 space-y-1.5 text-[12.5px]">
            <div className="flex justify-between"><span className="text-foreground/65">Items subtotal</span><span className="tabular-nums">{sampleMoney(itemsSubtotal)}</span></div>
            {pt && pt.billed > 0 && (
              <>
                <div className="flex justify-between">
                  <span className="text-foreground/65 inline-flex items-center gap-1.5"><Truck className="w-3 h-3"/>Shipping <span className="text-foreground/45">· {pt.carrier}</span></span>
                  <span className="tabular-nums">{sampleMoney(pt.billed)}</span>
                </div>
                {pt.markup_pct > 0 && (
                  <div className="text-[10.5px] text-foreground/45 pl-4 -mt-1">
                    Carrier {sampleMoney(pt.cost)} + {pt.markup_pct}% handling · <span className="font-mono">{pt.tracking}</span>
                  </div>
                )}
              </>
            )}
            <div className="border-t border-foreground/15 pt-1.5 mt-1.5 flex justify-between font-semibold">
              <span>Total due</span><span className="tabular-nums">{sampleMoney(invoice.amount)}</span>
            </div>
          </div>

          <div>
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Available actions <span className="font-normal normal-case ml-1.5 text-foreground/45">(disabled in sample mode)</span></div>
            <div className="flex flex-wrap gap-2">
              {invoice.status !== "paid" && <DisabledSampleAction icon={DollarSign}>Pay now</DisabledSampleAction>}
              <DisabledSampleAction icon={ExternalLink}>Download PDF</DisabledSampleAction>
              <DisabledSampleAction icon={Receipt}>Request credit memo</DisabledSampleAction>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sample Shipment detail drawer
// ─────────────────────────────────────────────────────────────────────────────
export function SampleShipmentDrawer({ shipment, onClose }) {
  if (!shipment) return null;
  const tone =
    shipment.status === "delivered"  ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" :
    shipment.status === "in_transit" ? "bg-blue-500/15 text-blue-700 border-blue-500/30" :
                                        "bg-foreground/10 text-foreground/65 border-foreground/15";
  return (
    <Sheet open={!!shipment} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-[560px] overflow-y-auto" data-testid="sample-shipment-drawer">
        <SheetCloseX/>
        <SheetHeader className="pb-3 border-b border-border">
          <SheetTitle className="display text-xl tracking-tight flex items-center gap-2 pr-8">
            Shipment {shipment.order_code}
            <SampleChip/>
          </SheetTitle>
          <div className="text-[12px] text-foreground/55">
            {shipment.carrier} {shipment.service} · {shipment.origin_city} → {shipment.destination_city}
          </div>
        </SheetHeader>
        <div className="py-4 space-y-5">
          <SampleDrawerExplainer kind="shipment"/>

          <div className="rounded-xl border border-border bg-white p-4 space-y-1.5 text-[12.5px]">
            <KV k="Status"          v={
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${tone}`}>
                {shipment.status.replace(/_/g, " ")}
              </span>
            }/>
            <KV k="Tracking number" v={<span className="font-mono break-all">{shipment.tracking_number}</span>}/>
            <KV k="Carrier"         v={`${shipment.carrier} · ${shipment.service}`}/>
            <KV k="ETA"             v={`${shipment.estimated_days} business day${shipment.estimated_days === 1 ? "" : "s"}`}/>
            <KV k="Weight"          v={`${shipment.weight} lbs`}/>
            <KV k="Cost"            v={sampleMoney(shipment.cost)}/>
            {shipment.delivered_at && (
              <KV k="Delivered" v={new Date(shipment.delivered_at).toLocaleString()}/>
            )}
          </div>

          {/* Tracking timeline */}
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-3">Tracking timeline</div>
            <div className="space-y-3">
              {[
                shipment.status === "delivered"  && { time: "Today 4:30 PM",   label: "Delivered",          tone: "emerald", icon: CheckCircle2 },
                                                     { time: "Today 10:15 AM",  label: "Out for delivery",   tone: "blue",    icon: Truck },
                                                     { time: "Yesterday",       label: "In transit · arrived at facility",  tone: "blue",    icon: Package },
                                                     { time: "2 days ago",      label: "Picked up by carrier", tone: "slate",   icon: MapPin },
                                                     { time: "2 days ago",      label: "Label printed",      tone: "slate",   icon: Receipt },
              ].filter(Boolean).map((ev, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`shrink-0 w-7 h-7 rounded-full grid place-items-center ${
                    ev.tone === "emerald" ? "bg-emerald-500/15 text-emerald-700" :
                    ev.tone === "blue"    ? "bg-blue-500/15 text-blue-700" :
                                             "bg-foreground/10 text-foreground/65"
                  }`}>
                    <ev.icon className="w-3.5 h-3.5"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12.5px] text-foreground/85">{ev.label}</div>
                    <div className="text-[10.5px] text-foreground/55 mt-0.5">{ev.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-2">Available actions <span className="font-normal normal-case ml-1.5 text-foreground/45">(disabled in sample mode)</span></div>
            <div className="flex flex-wrap gap-2">
              <DisabledSampleAction icon={MapPin}>Open carrier tracking</DisabledSampleAction>
              <DisabledSampleAction icon={AlertTriangle}>Report damage / shortage</DisabledSampleAction>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────────────
function SampleStat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3.5">
      <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 mb-1">{label}</div>
      <div className="text-[15px] font-semibold">{value}</div>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-foreground/55">{k}</span>
      <span className="text-foreground/85 text-right">{v}</span>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// SampleInsurancePreview — empty-state preview for the Seller Insurance
// dashboard. Compliance-aware: shows zero fake dollar amounts (Allianz is a
// real regulated insurance product, so we cannot imply coverage that doesn't
// exist). Instead shows the structural layout — carrier labeled "Sample",
// $ amounts as em-dashes, but utilization % (a structural concept, not money)
// is shown so the seller sees the visual treatment of a half-used policy.
//
// The compliance banner inside the preview is more prominent than other
// surfaces because of the legal sensitivity.
// ─────────────────────────────────────────────────────────────────────────────
export function SampleInsurancePreview() {
  return (
    <div data-testid="sample-insurance-preview" className="rounded-2xl border border-border bg-white overflow-hidden">
      <SamplePreviewBanner noun="Insurance" />
      <div className="px-5 py-3 bg-amber-50 border-b border-amber-200 text-[11.5px] text-amber-900 flex items-start gap-2">
        <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
        <div>
          <strong>Sample mode — no real coverage.</strong> All dollar amounts here are intentionally hidden (—) because trade-credit insurance is a regulated Allianz product. Once you connect a real buyer, this view shows live coverage limits and premiums.
        </div>
      </div>
      <div className="divide-y divide-border">
        {SAMPLE_INSURANCE_COVERS.map(c => (
          <SampleInsuranceRow key={c.buyer_id} cover={c} />
        ))}
      </div>
    </div>
  );
}

function SampleInsuranceRow({ cover }) {
  const tone =
    cover.source === "covered"        ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/20" :
    cover.source === "investigating"  ? "bg-amber-500/15 text-amber-800 border-amber-500/20" :
                                         "bg-foreground/10 text-foreground/55 border-foreground/15";
  const dotTone =
    cover.source === "covered"        ? "bg-emerald-500" :
    cover.source === "investigating"  ? "bg-amber-500 animate-pulse" :
                                         "bg-foreground/30";
  return (
    <div
      className="grid grid-cols-[2fr_180px_140px_140px_140px_120px] gap-4 px-5 py-4 items-center opacity-[0.78] cursor-default"
      data-testid={`sample-insurance-row-${cover.buyer_id}`}
      title="Sample row — actions disabled until you have real buyers"
    >
      {/* Buyer */}
      <div className="min-w-0 flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${dotTone}`}/>
        <div className="min-w-0">
          <div className="font-medium text-sm truncate text-slate-700 flex items-center gap-1.5">
            {cover.buyer_name}
            <SampleChip />
          </div>
          <div className="text-[11px] text-foreground/55 truncate">
            {cover.buyer_email}{cover.score ? ` · score ${cover.score}` : ""}
          </div>
        </div>
      </div>
      {/* Status */}
      <div>
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${tone}`}>
          {cover.status_label}
        </span>
        <div className="text-[10px] text-foreground/50 mt-1 mono uppercase tracking-wider">Carrier: {cover.carrier}</div>
      </div>
      {/* Coverage limit */}
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Limit</div>
        <div className="text-[13px] tabular-nums text-foreground/85">{cover.limit_display}</div>
      </div>
      {/* Utilization (% is structural, OK to show) */}
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/55 flex justify-between"><span>Used</span><span>{cover.used_pct}%</span></div>
        <div className="text-[13px] tabular-nums text-foreground/85">{cover.used_display}</div>
        <div className="mt-1 h-1 rounded-full bg-foreground/10 overflow-hidden">
          <div className="h-full bg-foreground/55" style={{ width: `${cover.used_pct}%` }}/>
        </div>
      </div>
      {/* Premium */}
      <div>
        <div className="text-[10px] mono uppercase tracking-widest text-foreground/55">Premium / yr</div>
        <div className="text-[13px] tabular-nums text-foreground/85">{cover.premium_display}</div>
        <div className="text-[10px] text-foreground/50 mt-0.5">{cover.expires_label}</div>
      </div>
      {/* Actions — disabled */}
      <div className="text-right">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider text-foreground/40 border border-foreground/15">
          <Ban className="w-2.5 h-2.5"/>Sample
        </span>
      </div>
    </div>
  );
}

